import { useEffect, useState } from 'react';
import { Megaphone, Plus, Loader2, Trash2, Edit2, Users, ExternalLink, CheckCircle, XCircle, Clock, Star, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const emptyForm = { title: '', description: '', event_id: '', deadline: '', spots: '', time_slots: [] };

export default function OpenCallsAdminPage() {
  const [calls, setCalls] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [viewingApps, setViewingApps] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchCalls = async () => {
    try {
      const res = await api.get('/dj/admin/open-calls');
      setCalls(res.data?.open_calls || []);
    } catch { toast.error('Error al cargar convocatorias'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchCalls();
    api.get('/events').then((res) => {
      const list = Array.isArray(res.data) ? res.data : res.data?.events || [];
      setEvents(list);
    }).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (call) => {
    setEditing(call);
    setForm({
      title: call.title || '',
      description: call.description || '',
      event_id: call.event_id || '',
      deadline: call.deadline ? String(call.deadline).slice(0, 16) : '',
      spots: call.spots || '',
      time_slots: (call.time_slots || []).map((s) => ({ ...s })),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        event_id: form.event_id || null,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        spots: form.spots ? Number(form.spots) : null,
        time_slots: form.time_slots,
      };
      if (editing) {
        await api.put(`/dj/admin/open-calls/${editing.id}`, payload);
        toast.success('Convocatoria actualizada');
      } else {
        await api.post('/dj/admin/open-calls', payload);
        toast.success('Convocatoria creada y publicada en el Portal DJ');
      }
      setShowModal(false);
      fetchCalls();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al guardar');
    } finally { setSubmitting(false); }
  };

  const toggleStatus = async (call) => {
    try {
      await api.put(`/dj/admin/open-calls/${call.id}`, { status: call.status === 'open' ? 'closed' : 'open' });
      toast.success(call.status === 'open' ? 'Convocatoria cerrada' : 'Convocatoria reabierta');
      fetchCalls();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const handleDelete = async (call) => {
    if (!window.confirm(`¿Eliminar "${call.title}" y todas sus postulaciones?`)) return;
    try {
      await api.delete(`/dj/admin/open-calls/${call.id}`);
      toast.success('Convocatoria eliminada');
      fetchCalls();
    } catch { toast.error('Error al eliminar'); }
  };

  const viewApplications = async (call) => {
    setViewingApps(call);
    setAppsLoading(true);
    try {
      const res = await api.get(`/dj/admin/open-calls/${call.id}/applications`);
      setApplications(res.data?.applications || []);
    } catch { toast.error('Error al cargar postulantes'); }
    finally { setAppsLoading(false); }
  };

  const setAppStatus = async (app, status) => {
    if (status === 'confirmed') {
      const msg = app.status === 'confirmed'
        ? `¿Generar la placa de ${app.dj_name} y reenviarle el email con la imagen?`
        : `¿Confirmar la participación de ${app.dj_name}? Se genera su placa para Instagram y se le envía por email.`;
      if (!window.confirm(msg)) return;
      setConfirmingId(app.id);
    }
    try {
      const res = await api.put(`/dj/admin/open-calls/${viewingApps.id}/applications/${app.id}?status=${status}`);
      setApplications((prev) => prev.map((a) => (a.id === app.id
        ? { ...a, status, ...(res.data?.share_images ? { share_images: res.data.share_images } : {}) }
        : a)));
      if (status === 'confirmed') {
        toast.success(res.data?.share_images
          ? 'Participación confirmada — placa generada y enviada al DJ'
          : 'Participación confirmada — email enviado (sin placa: falta flyer del evento)');
      } else {
        toast.success(status === 'accepted' ? 'Postulante aceptado' : 'Postulante rechazado');
      }
    } catch { toast.error('Error al actualizar'); }
    finally { setConfirmingId(null); }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black flex items-center gap-2">
              <Megaphone className="text-[#D4AF37]" /> Convocatorias DJ
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Open Calls que se anuncian en el Portal DJ y el inicio. Los DJs se postulan con su Press Kit.</p>
          </div>
          <Button onClick={openCreate} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold" data-testid="create-open-call-btn">
            <Plus size={16} className="mr-1" /> Nueva Convocatoria
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#D4AF37]" /></div>
        ) : calls.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">
            <Megaphone size={48} className="mx-auto mb-4 opacity-40" />
            <p>No hay convocatorias. Creá la primera para anunciar tu Open Call.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {calls.map((call) => (
              <div key={call.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid={`admin-open-call-${call.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-lg">{call.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${call.status === 'open' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-700 text-zinc-400'}`}>
                        {call.status === 'open' ? 'Abierta' : 'Cerrada'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 line-clamp-2">{call.description}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-500">
                      {call.event && <span>🎫 {call.event.title}</span>}
                      {call.deadline && <span className="flex items-center gap-1"><Clock size={11} /> Cierra {new Date(call.deadline).toLocaleDateString('es-AR')}</span>}
                      {call.spots && <span>Cupo: {call.spots}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <button
                      onClick={() => viewApplications(call)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] rounded-lg text-sm font-bold hover:bg-[#D4AF37]/25 transition-colors"
                      data-testid={`view-applications-${call.id}`}
                    >
                      <Users size={14} /> {call.applications_count || 0} postulantes
                    </button>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEdit(call)} className="border-zinc-700 h-8 px-2"><Edit2 size={13} /></Button>
                      <Button variant="outline" size="sm" onClick={() => toggleStatus(call)} className="border-zinc-700 h-8 px-2 text-xs">
                        {call.status === 'open' ? 'Cerrar' : 'Reabrir'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(call)} className="border-red-500/40 text-red-400 h-8 px-2"><Trash2 size={13} /></Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? 'Editar Convocatoria' : 'Nueva Convocatoria'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-zinc-400 text-xs">Título *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Open Call — Warm Up Chris Luno On Board" className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-title" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Descripción *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Buscamos un DJ para abrir la fecha... requisitos, estilo, duración del set, etc." className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-description" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Evento vinculado (opcional)</Label>
                <Select value={form.event_id || 'none'} onValueChange={(v) => setForm({ ...form, event_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-event">
                    <SelectValue placeholder="Sin evento" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">Sin evento</SelectItem>
                    {events.map((ev) => (
                      <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Fecha límite (opcional)</Label>
                  <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-deadline" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Cupo de postulaciones (0 = ∞)</Label>
                  <Input type="number" min={0} value={form.spots} onChange={(e) => setForm({ ...form, spots: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-spots" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-xs">Horarios buscados (opcional)</Label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, time_slots: [...form.time_slots, { label: '', start_time: '', end_time: '' }] })}
                    className="text-xs text-[#D4AF37] font-bold hover:underline"
                    data-testid="open-call-add-slot"
                  >
                    + Agregar horario
                  </button>
                </div>
                {form.time_slots.length === 0 ? (
                  <p className="text-[11px] text-zinc-600 mt-1">Sin horarios: los DJs se postulan a la convocatoria general</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {form.time_slots.map((slot, idx) => (
                      <div key={slot.id || idx} className="grid grid-cols-[1fr,1fr,1.4fr,auto] gap-2 items-center" data-testid={`open-call-slot-row-${idx}`}>
                        <Input type="time" value={slot.start_time || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, start_time: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" data-testid={`open-call-slot-start-${idx}`} />
                        <Input type="time" value={slot.end_time || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, end_time: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" data-testid={`open-call-slot-end-${idx}`} />
                        <Input placeholder="Etiqueta (ej: Warm Up, Closing)" value={slot.label || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, label: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" data-testid={`open-call-slot-label-${idx}`} />
                        <button type="button" onClick={() => setForm({ ...form, time_slots: form.time_slots.filter((_, i) => i !== idx) })} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`open-call-slot-remove-${idx}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <p className="text-[11px] text-zinc-600">Los DJs eligen a qué horario postularse (o a todos). Editar no borra postulaciones.</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button
                onClick={handleSave}
                disabled={submitting || form.title.trim().length < 3 || form.description.trim().length < 10}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
                data-testid="open-call-save"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {editing ? 'Guardar' : 'Publicar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Applications modal */}
        <Dialog open={!!viewingApps} onOpenChange={(o) => !o && setViewingApps(null)}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Postulantes: {viewingApps?.title}</DialogTitle>
            </DialogHeader>
            {appsLoading ? (
              <div className="flex justify-center py-10"><Loader2 size={28} className="animate-spin text-[#D4AF37]" /></div>
            ) : applications.length === 0 ? (
              <p className="text-zinc-500 text-sm py-6 text-center">Todavía no hay postulaciones.</p>
            ) : (
              <div className="space-y-3 py-2">
                {applications.map((app) => (
                  <div key={app.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`application-${app.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {app.dj_photo_url ? <img src={app.dj_photo_url} alt="" className="w-full h-full object-cover" /> : <Users size={20} className="text-[#D4AF37]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold">{app.dj_name}</p>
                          {app.agency_name && (
                            <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/30 text-[10px] font-bold uppercase">Agencia: {app.agency_name}</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            app.status === 'confirmed' ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40' : app.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-400' : app.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-yellow-500/15 text-yellow-400'
                          }`}>
                            {app.status === 'confirmed' ? '★ Confirmado' : app.status === 'accepted' ? 'Seleccionado' : app.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {app.dj_city} · {(app.dj_genres || []).slice(0, 4).join(', ')} {app.dj_instagram ? `· ${app.dj_instagram}` : ''}
                        </p>
                        {(viewingApps?.time_slots?.length > 0) && (
                          <p className="text-xs mt-1" data-testid={`app-slots-${app.id}`}>
                            <Clock size={10} className="inline mr-1 text-[#D4AF37]" />
                            <span className="text-[#D4AF37] font-bold">
                              {app.slot_labels?.length ? app.slot_labels.join(' · ') : 'Todos los horarios'}
                            </span>
                          </p>
                        )}
                        {app.message && <p className="text-sm text-zinc-300 mt-2 bg-zinc-800/60 rounded-lg p-2.5 whitespace-pre-line">{app.message}</p>}
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          {app.dj_slug && (
                            <a href={`/dj/${app.dj_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline">
                              <ExternalLink size={12} /> Ver Press Kit
                            </a>
                          )}
                          <div className="flex-1" />
                          {(app.status === 'accepted' || (app.status === 'confirmed' && !app.share_images)) && (
                            <Button size="sm" onClick={() => setAppStatus(app, 'confirmed')} disabled={confirmingId === app.id} className="h-7 px-3 bg-[#D4AF37] hover:bg-[#B8962E] text-black text-xs font-bold" data-testid={`confirm-app-${app.id}`}>
                              {confirmingId === app.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Star size={12} className="mr-1" />}
                              {confirmingId === app.id ? 'Generando placa...' : app.status === 'confirmed' ? 'Generar placa' : 'Confirmar participación'}
                            </Button>
                          )}
                          <Button size="sm" onClick={() => setAppStatus(app, 'accepted')} disabled={app.status === 'accepted' || app.status === 'confirmed'} className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`accept-app-${app.id}`}>
                            <CheckCircle size={12} className="mr-1" /> Aceptar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setAppStatus(app, 'rejected')} disabled={app.status === 'rejected' || app.status === 'confirmed'} className="h-7 px-3 border-red-500/40 text-red-400 text-xs">
                            <XCircle size={12} className="mr-1" /> Rechazar
                          </Button>
                        </div>
                        {app.status === 'confirmed' && app.share_images && (
                          <div className="flex items-center gap-3 mt-2" data-testid={`share-images-${app.id}`}>
                            <ImageIcon size={13} className="text-[#D4AF37]" />
                            {app.share_images.post && (
                              <a href={app.share_images.post} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] hover:underline">Placa Post 1:1</a>
                            )}
                            {app.share_images.story && (
                              <a href={app.share_images.story} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] hover:underline">Placa Story 9:16</a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
